import clinicalAssessment from './questionnaires/clinical-assessment';
import medicationDSS from './questionnaires/medication-dss';

const isRequired = {
  method(value) {
    return (value !== null) && (value !== '');
  },
  key: 'IS_REQUIRED',
};

const isNumeric = {
  method(value) {
    return /^\d+$/.test(value);
  },
  key: 'IS_NUMERIC',
  labelTranslation: {key : 'dummy.general.invalid_whole_number', catalog : 'validators'}
};

const isDecimal = {
  method(value) {
    return /^\d+(\.\d{1,2})?$/.test(value);
  },
  key: 'IS_DECIMAL',
  labelTranslation: {key : 'dummy.general.invalid_number', catalog : 'validators'}
};

const isSystolicBloodPressure = {
  method(value) {
    return (parseInt(value) >= 40) && (parseInt(value) <= 280);
  },
  key: 'IS_SYSTOLIC_BLOOD_PRESSURE',
  labelTranslation: {key : 'dummy.healthparameters.bloodpressure.systolic_invalid_range', catalog : 'validators'}
};

const isDiastolicBloodPressure = {
  method(value) {
    return (parseInt(value) >= 30) && (parseInt(value) <= 160);
  },
  key: 'IS_DIASTOLIC_BLOOD_PRESSURE',
  labelTranslation: {key : 'dummy.healthparameters.bloodpressure.diastolic_invalid_range', catalog : 'validators'}
};

const isLDL = {
  method(value, store) {
    const preferredUnit = store.getters['user/preferredLipidsUnit'];
    value = value / 1;

    if (preferredUnit === 'mmol/L') {
      return value >= 0.2586 && value <= 11.63693;
    }else if (preferredUnit === 'mg/dL') {
      return value >= 10 && value <= 450;
    }else{
      throw new Error('Unknown preferred lipis unit for user: ' + preferredUnit);
    }
  },
  key: 'IS_LDL',
  labelTranslation: {key : 'reportParametersBloodTestScreen.errorLdlUnrealisticNumber', catalog : 'app_mobile'}
};

const isHDL = {
  method(value) {
    return (parseInt(value) >= 10) && (parseInt(value) <= 200);
  },
  key: 'IS_HDL',
  labelTranslation: {key : 'reportParametersBloodTestScreen.errorCHdlUnrealisticNumber', catalog : 'app_mobile'}
};

const isCholesterol = {
  method(value) {
    return (parseInt(value) >= 50) && (parseInt(value) <= 500);
  },
  key: 'IS_CHOLESTEROL',
  labelTranslation: {key : 'reportParametersBloodTestScreen.errorCholesterolUnrealisticNumber', catalog : 'app_mobile'}
};

const isHbA1c = {
  method(value) {
    return (parseFloat(value) >= 2.15) && (parseFloat(value) <= 20);
  },
  key: 'IS_HBA1C',
  labelTranslation: {key : 'reportParametersBloodTestScreen.errorGlucoseUnrealisticNumber', catalog : 'app_mobile'}
};

const isBodyWeight = {
  // weight (kg) / (height (m) * height (m)) = 60 (max BMI)
  // maxWeight (kg) = (height (m) * height (m)) * 60 (max BMI)
  // minWeight (kg) = (height (m) * height (m)) * 12 (max BMI)
  method(weight, store) {
    let height = store.state?.patientSummary?.length?.valueQuantity?.value;

    if (height) {
      height = parseFloat(height);
      weight = parseFloat(weight);
      const heightM = height/100;

      const BMI = weight / (heightM * heightM);

      return BMI >= 12 && BMI <= 60;
    }
    return (weight >= 1) && (weight <= 450);
  },
  key: 'IS_BODY_WEIGHT',
  labelTranslation: {key : 'reportParametersHomeScreen.errorWeightUnrealisticNumber', catalog : 'app_mobile'}
};

const isPulseRate = {
  method(value) {
    return (parseInt(value) >= 35) && (parseInt(value) <= 140);
  },
  key: 'IS_PULSE_RATE',
};

const isAdult = {
  method(value) {
    return parseInt(value) >= 18;
  },
  key: 'IS_ADULT',
};

const isDistanceWalked = {
  method(value) {
    return (parseInt(value) >= 100) && (parseInt(value) <= 999);
  },
  key: 'IS_DISTANCE_WALKED',
}

export function getFailedValidations(rules, value, store) {
  return rules.reduce((failedValidations, currentRule, index) => {
    if (currentRule.method(value, store)) {
      return failedValidations;
    }

    return [...failedValidations, index];
  }, []);
}

export default {
  bodyWeight: {
    page: 'PatientEncounterVitalSigns',
    rules: [isRequired, isBodyWeight, isDecimal],
  },
  LDLInPreferredUnit: {
    page: 'PatientEncounterMedicationDSS',
    rules: [isRequired, isLDL, isDecimal],
  },
  eGFR: {
    page: 'PatientEncounterMedicationDSS',
    rules: [isRequired],
  },
  bloodPressureSystolic: {
    page: 'PatientEncounterVitalSigns',
    rules: [isRequired, isNumeric, isSystolicBloodPressure],
  },
  bloodPressureDiastolic: {
    page: 'PatientEncounterVitalSigns',
    rules: [isRequired, isNumeric, isDiastolicBloodPressure],
  },
  pulseRate: {
    page: 'PatientEncounterVitalSigns',
    rules: [isRequired, isNumeric, isPulseRate],
  },
  distanceWalked: {
    page: 'PatientEncounterWalkingTest',
    rules: [isRequired, isNumeric, isDistanceWalked],
  },
  perceivedExertion: {
    page: 'PatientEncounterWalkingTest',
    rules: [isRequired],
  },
  isWalkingTestPerformed: {
    page: 'PatientEncounterWalkingTest',
    rules: [isRequired],
  },
  notPerformedReasonGroup: {
    page: 'PatientEncounterWalkingTest',
    rules: [isRequired],
  },
  notPerformedReason: {
    page: 'PatientEncounterWalkingTest',
    rules: [isRequired],
  },
  ...clinicalAssessment.reduce((accumulator, currentItem) => ({
    ...accumulator, [currentItem.key]: {
      page: 'PatientEncounterClinicalAssessment',
      rules: [isRequired, ...(currentItem.key === 'AGE' ? [isNumeric, isAdult] : [])],
    },
  }), {}),
  ...medicationDSS.reduce((accumulator, currentItem) => ({
    ...accumulator, [currentItem.key]: {
      page: 'PatientEncounterMedicationDSS',
      rules: [isRequired],
    },
  }), {}),
};
